import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Textarea } from '@/components/ui/textarea'
import { ModeType, RoomModalMode } from '@/redux/types/room'
import { TimerModeSelector } from '@/components/settings/timer-configuration/TimerModeSelector'
import { TimerMode } from '@/components/settings/timer-configuration/types'

interface CreateRoomModalProps {
  modeType: ModeType
  isOpen: boolean
  onClose: () => void
  onConfirm: (roomName: string, timerMode?: TimerMode, description?: string) => void
  isLoading: boolean
  mode?: RoomModalMode
  currentRoomName?: string
  currentDescription?: string
}

const CreateRoomModal: React.FC<CreateRoomModalProps> = ({
  modeType,
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  mode = RoomModalMode.CREATE,
  currentRoomName = '',
  currentDescription = '',
}) => {
  const [roomName, setRoomName] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [timerMode, setTimerMode] = useState<TimerMode>(TimerMode.SINGLE_ROUND)

  const isEditMode = mode === RoomModalMode.EDIT
  const isRoomMode = modeType === ModeType.ROOM

  useEffect(() => {
    if (isOpen) {
      setRoomName(isEditMode ? currentRoomName : '')
      setDescription(isEditMode && !isRoomMode ? currentDescription : '')
      if (!isEditMode) {
        setTimerMode(TimerMode.SINGLE_ROUND)
      }
    }
  }, [isOpen, isEditMode, isRoomMode, currentRoomName, currentDescription])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmedName = roomName.trim()

    if (trimmedName && (!isEditMode || trimmedName !== currentRoomName || description.trim() !== currentDescription)) {
      if (isRoomMode) {
        onConfirm(trimmedName, timerMode)
      } else {
        onConfirm(trimmedName, undefined, description.trim())
      }
      if (!isEditMode) {
        setRoomName('')
        setDescription('')
        setTimerMode(TimerMode.SINGLE_ROUND)
      }
    }
  }

  const handleClose = () => {
    setRoomName('')
    setDescription('')
    setTimerMode(TimerMode.SINGLE_ROUND)
    onClose()
  }

  const isButtonDisabled = isEditMode
    ? !roomName.trim() ||
      (roomName.trim() === currentRoomName &&
        description.trim() === currentDescription) ||
      isLoading
    : !roomName.trim() || isLoading

  const getTitle = () => {
    if (isEditMode) {
      return isRoomMode ? 'Edit Room Name' : 'Edit Team Name'
    }
    return isRoomMode ? 'Create a New Room' : 'Create a New Team'
  }

  const getDescription = () => {
    if (isEditMode) {
      return isRoomMode
        ? 'Update the name for this game room.'
        : 'Update the name for this team.'
    }
    return isRoomMode
      ? 'Enter a name for your new game room.'
      : 'Enter a name for your new team.'
  }

  const getLoadingText = () => (isEditMode ? 'Updating...' : 'Creating...')

  const getButtonText = () => {
    if (isEditMode) {
      return isRoomMode ? 'Update Room' : 'Update Team'
    }
    return isRoomMode ? 'Create Room' : 'Create Team'
  }

  const getLabelText = () => (isRoomMode ? 'Room Name' : 'Team Name')

  const getPlaceholderText = () =>
    isRoomMode ? 'Enter room name' : 'Enter team name'

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className='sm:max-w-[425px]'>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{getTitle()}</DialogTitle>
            <DialogDescription>{getDescription()}</DialogDescription>
          </DialogHeader>
          <div className='grid gap-4 py-4'>
            <div className='grid grid-cols-4 items-center gap-4'>
              <Label htmlFor='room-name'>{getLabelText()}</Label>
              <Input
                id='room-name'
                className='col-span-3'
                placeholder={getPlaceholderText()}
                value={roomName}
                onChange={(e) => setRoomName(e.target.value)}
                autoFocus
                required
              />
            </div>
            {!isRoomMode && (
              <div className='grid grid-cols-4 items-start gap-4'>
                <Label htmlFor='team-description' className='pt-2'>
                  Description
                </Label>
                <Textarea
                  id='team-description'
                  className='col-span-3 resize-none'
                  placeholder='Enter team description (optional)'
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                />
              </div>
            )}
            {!isEditMode && isRoomMode && (
              <>
                <TimerModeSelector
                  mode={timerMode}
                  onModeChange={setTimerMode}
                  disabled={isLoading}
                  showSingleAdvisorRound={true}
                  inline={true}
                />
                {timerMode === TimerMode.SINGLE_ROUND_ADVISOR_ONLY ? (
                  <Alert className='bg-sky-50 border-sky-200 text-sky-900'>
                    <AlertDescription className='text-sm'>
                      <strong>Note:</strong> If Single Round (Advisor Only) mode
                      is selected, it cannot be changed later.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert className='bg-amber-50 border-amber-200 text-amber-900'>
                    <AlertDescription className='text-sm'>
                      <strong>Note:</strong> Once created, you cannot switch to
                      Single Round (Advisor Only) mode later.
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}
          </div>
          <DialogFooter className='flex gap-2 sm:justify-end'>
            <Button
              type='button'
              variant='outline'
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              className='bg-dark-blue hover:bg-dark-blue-hovered'
              type='submit'
              disabled={isButtonDisabled}
            >
              {isLoading ? getLoadingText() : getButtonText()}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default CreateRoomModal
